import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SiteBackground } from "./SiteBackground";
import { UsageBar } from "./UsageBar";
import { Home, ScanLine, Ruler, Bookmark, Settings as Cog, LogOut, Compass, MessageSquare, FileText, ShieldCheck, TrendingUp, Tag, Hammer, Briefcase } from "lucide-react";

const nav = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/trends", label: "Trends", icon: TrendingUp, testid: "trends" },
  { to: "/ask", label: "Talk to the Mentor", icon: MessageSquare, testid: "ask" },
  { to: "/scan", label: "Scan", icon: ScanLine },
  { to: "/measure", label: "Measure", icon: Ruler },
  { to: "/build", label: "Design & Build It", icon: Hammer, testid: "build" },
  { to: "/listings", label: "Products & Services", icon: Tag, testid: "listings" },
  { to: "/jobs", label: "Job Offers", icon: Briefcase, testid: "jobs" },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/saved", label: "Saved", icon: Bookmark },
  { to: "/settings", label: "Settings", icon: Cog },
];

export function Layout({ children }) {
  const loc = useLocation();
  const nav_ = useNavigate();
  const { user, logout } = useAuth();
  const isAsk = loc.pathname === "/ask";
  const navItems = user?.role === "admin"
    ? [...nav, { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "admin" }]
    : nav;

  return (
    <div className="min-h-screen flex flex-col md:flex-row relative" style={{ background: "var(--bg)" }}>
      <SiteBackground variant={isAsk ? "ask" : "office"} />
      <aside className="md:w-60 md:min-h-screen border-b md:border-b-0 md:border-r relative z-10" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="p-5 md:p-6 flex md:block items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <Compass className="w-6 h-6" strokeWidth={2.2} />
            <span className="font-head font-extrabold text-lg tracking-tight">Path Mentor</span>
          </Link>
          <button data-testid="mobile-logout-btn" onClick={async () => { await logout(); nav_("/"); }}
            className="md:hidden pm-btn-ghost text-sm flex items-center gap-1.5 py-1.5 px-3">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <nav className="flex md:flex-col gap-1 px-3 pb-3 overflow-x-auto">
          {navItems.map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to;
            return (
              <Link key={to} to={to} data-testid={`nav-${testid || label.toLowerCase()}`}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
                style={active ? { background: "var(--text)", color: "#fff" } : { color: "var(--text-2)" }}>
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </nav>
        <div className="hidden md:block absolute bottom-0 w-60 p-3 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="label-mono mb-2 truncate">{user?.is_guest ? "Guest" : user?.email}</div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav_("/"); }}
            className="pm-btn-ghost text-sm w-full flex items-center justify-center gap-2 py-2">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <UsageBar />
          {isAsk ? children : <div className="pm-sheet p-4 sm:p-7">{children}</div>}
        </div>
      </main>
    </div>
  );
}
